import {
  Avatar,
  Card,
  Chip,
  Divider,
  Grid,
  Icon,
  List,
  ListItem,
  Stack,
} from "@mui/material";
import AccessDenied from "components/AccessDenied";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import appLogsService from "services/app-logs-service";

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function InfoRow({ label, value }) {
  return (
    <MDBox display="flex" justifyContent="space-between" gap={2}>
      <MDTypography variant="button" color="text">
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight="regular">
        {value ?? "-"}
      </MDTypography>
    </MDBox>
  );
}

function formatBytes(value) {
  const size = Number(value);

  if (!Number.isFinite(size) || size <= 0) {
    return "-";
  }

  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1,
  );
  const formatted = size / 1024 ** unitIndex;

  return `${formatted.toFixed(formatted >= 10 || unitIndex === 0 ? 0 : 1)} ${
    units[unitIndex]
  }`;
}

function normalizeFiles(upload, type) {
  const arrayCandidates =
    type === "log"
      ? [upload.logFiles, upload.logs, upload.files]
      : [upload.databaseFiles, upload.dbFiles, upload.databases];
  const items = arrayCandidates.find(Array.isArray);

  if (items?.length) {
    return items
      .map((file, index) => ({
        id: file.id || `${type}-${upload.id || "upload"}-${index}`,
        name:
          file.originalName ||
          file.name ||
          file.fileName ||
          file.filename ||
          "-",
        url: file.url || file.downloadUrl || file.path || null,
        uploadedAt: file.createdAt || file.uploadedAt || upload.createdAt || null,
        size: file.size || file.fileSize || null,
        mimeType: file.mimeType || file.type || null,
      }))
      .filter((file) => file.url || file.name !== "-");
  }

  if (type === "log" && (upload.logUrl || upload.logOriginalName)) {
    return [
      {
        id: `log-${upload.id || "upload"}`,
        name: upload.logOriginalName || "-",
        url: upload.logUrl || null,
        uploadedAt: upload.createdAt || null,
        size: upload.logSize || null,
        mimeType: upload.logMimeType || null,
      },
    ];
  }

  if (
    type === "database" &&
    (upload.databaseUrl || upload.databaseOriginalName)
  ) {
    return [
      {
        id: `db-${upload.id || "upload"}`,
        name: upload.databaseOriginalName || "-",
        url: upload.databaseUrl || null,
        uploadedAt: upload.createdAt || null,
        size: upload.databaseSize || null,
        mimeType: upload.databaseMimeType || null,
      },
    ];
  }

  return [];
}

function FileGroup({ title, emptyText, files, downloadLabel }) {
  async function handleDownload(file) {
    if (!file.url) {
      return;
    }

    const response = await fetch(file.url);
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = objectUrl;
    link.download = file.name || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  }

  return (
    <MDBox>
      <MDTypography variant="button">{title}</MDTypography>

      {!files.length ? (
        <MDTypography variant="caption" color="text" display="block" mt={0.75}>
          {emptyText}
        </MDTypography>
      ) : (
        <Stack spacing={1.5} mt={1.25}>
          {files.map((file) => (
            <MDBox
              key={file.id}
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              flexDirection={{ xs: "column", md: "row" }}
              gap={1.5}
              p={1.5}
              borderRadius="lg"
              bgColor="grey-100"
            >
              <MDBox>
                <MDTypography variant="button">{file.name || "-"}</MDTypography>
                <MDBox
                  mt={0.5}
                  display="flex"
                  gap={1}
                  flexWrap="wrap"
                  alignItems="center"
                >
                  {file.uploadedAt ? (
                    <Chip
                      size="small"
                      label={`Uploaded ${formatDateTime(file.uploadedAt)}`}
                    />
                  ) : null}
                  {file.size ? (
                    <Chip size="small" label={formatBytes(file.size)} />
                  ) : null}
                  {file.mimeType ? (
                    <Chip size="small" label={file.mimeType} />
                  ) : null}
                </MDBox>
              </MDBox>

              {file.url ? (
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={() => handleDownload(file)}
                >
                  <Icon>download</Icon>&nbsp;{downloadLabel}
                </MDButton>
              ) : (
                <MDTypography variant="caption" color="text">
                  Download unavailable
                </MDTypography>
              )}
            </MDBox>
          ))}
        </Stack>
      )}
    </MDBox>
  );
}

function StatCard({ label, value }) {
  return (
    <MDBox
      px={2}
      py={1.5}
      borderRadius="lg"
      sx={{
        backgroundColor: "rgba(255,255,255,0.12)",
        border: "1px solid rgba(255,255,255,0.18)",
        minWidth: 140,
      }}
    >
      <MDTypography variant="caption" color="white" opacity={0.8}>
        {label}
      </MDTypography>
      <MDTypography variant="h6" color="white">
        {value ?? "-"}
      </MDTypography>
    </MDBox>
  );
}

export default function UserLogsDetails({ canAccess }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);

  useEffect(() => {
    if (!canAccess) {
      return;
    }

    const source = axios.CancelToken.source();

    appLogsService
      .getUserUploads(userId, source.token)
      .then(([response, data]) => {
        if (response.ok) {
          setPayload(data);
          setErrorStatus(null);
          return;
        }

        setErrorStatus(response.status);
      });

    return () => {
      source.cancel("Component unmounted");
    };
  }, [canAccess, userId]);

  const uploads = useMemo(() => {
    return [...(payload?.uploads || [])]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .map((upload) => ({
        ...upload,
        logFilesNormalized: normalizeFiles(upload, "log"),
        databaseFilesNormalized: normalizeFiles(upload, "database"),
      }));
  }, [payload]);

  if (!canAccess || errorStatus === 403) {
    return <AccessDenied type="component" />;
  }

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <MDButton
            variant="text"
            color="info"
            onClick={() => navigate("/user-logs")}
            sx={{ mb: 2 }}
          >
            <Icon>arrow_back</Icon>&nbsp;Back to User Logs
          </MDButton>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <MDBox
              p={{ xs: 2.5, md: 3 }}
              variant="gradient"
              bgColor="info"
              borderRadius="xl"
              coloredShadow="info"
            >
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", lg: "center" }}
                flexDirection={{ xs: "column", lg: "row" }}
                gap={3}
              >
                <MDBox display="flex" alignItems="center" gap={2.5}>
                  <Avatar
                    sx={{
                      width: 72,
                      height: 72,
                      border: "2px solid rgba(255,255,255,0.24)",
                    }}
                    src={payload?.user?.profileImage || undefined}
                  >
                    {(payload?.user?.name || userId || "U")?.[0]}
                  </Avatar>
                  <MDBox>
                    <MDTypography variant="h4" color="white">
                      {payload?.user?.name || userId}
                    </MDTypography>
                    <MDTypography variant="button" color="white" opacity={0.8}>
                      {payload?.user?.email || "-"}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="white"
                      opacity={0.7}
                      display="block"
                      mt={0.75}
                    >
                      User activity log details
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox
                  display="flex"
                  gap={1.5}
                  flexWrap="wrap"
                  justifyContent={{ xs: "flex-start", lg: "flex-end" }}
                >
                  <StatCard label="User ID" value={payload?.user?.id ?? userId} />
                  <StatCard
                    label="Log Records"
                    value={payload ? String(uploads.length) : "-"}
                  />
                  <StatCard
                    label="Latest Upload"
                    value={uploads[0] ? formatDateTime(uploads[0].createdAt) : "-"}
                  />
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Uploaded Entries
              </MDTypography>
            </MDBox>

            {errorStatus && errorStatus !== 403 ? (
              <MDBox p={3}>
                <MDTypography color="error">
                  Unable to load uploads for this user.
                </MDTypography>
              </MDBox>
            ) : !payload ? (
              <MDBox p={3}>
                <MDTypography color="text">Loading...</MDTypography>
              </MDBox>
            ) : !uploads.length ? (
              <MDBox p={3}>
                <MDTypography color="text">
                  No uploaded logs were found for this user.
                </MDTypography>
              </MDBox>
            ) : (
              <List sx={{ px: 2, py: 1 }}>
                {uploads.map((upload, index) => (
                  <ListItem
                    key={upload.id}
                    sx={{
                      display: "block",
                      px: { xs: 0.5, md: 1 },
                      py: 2,
                    }}
                  >
                    <MDBox
                      p={{ xs: 2, md: 2.5 }}
                      borderRadius="xl"
                      sx={{
                        backgroundColor: "#f8fafc",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                        flexDirection={{ xs: "column", md: "row" }}
                        gap={2}
                      >
                        <MDBox>
                          <MDTypography variant="h6">
                            {formatDateTime(upload.createdAt)}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            Upload #{upload.id}
                          </MDTypography>
                        </MDBox>
                        <MDBox display="flex" gap={1} flexWrap="wrap">
                          <Chip
                            size="small"
                            label={`Logs ${upload.logFilesNormalized.length}`}
                          />
                          <Chip
                            size="small"
                            label={`DB ${upload.databaseFilesNormalized.length}`}
                          />
                        </MDBox>
                      </MDBox>

                      <MDBox
                        display="grid"
                        gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
                        gap={1.5}
                        mt={2}
                      >
                        <InfoRow label="Email" value={upload.userEmail} />
                        <InfoRow label="Device ID" value={upload.deviceId} />
                        <InfoRow label="Device Name" value={upload.deviceName} />
                        <InfoRow label="Device OS" value={upload.deviceOS} />
                      </MDBox>

                      <MDBox
                        mt={2.5}
                        display="flex"
                        flexDirection="column"
                        gap={2}
                      >
                        <FileGroup
                          title="Log Files"
                          emptyText="No log files were uploaded for this record."
                          files={upload.logFilesNormalized}
                          downloadLabel="Download Log"
                        />
                        <FileGroup
                          title="Database Files"
                          emptyText="No database files were uploaded for this record."
                          files={upload.databaseFilesNormalized}
                          downloadLabel="Download DB"
                        />
                      </MDBox>
                    </MDBox>

                    {index < uploads.length - 1 && <Divider sx={{ mt: 3 }} />}
                  </ListItem>
                ))}
              </List>
            )}
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
